import type { ChatMessage } from '@/hooks/useShakespeare';
import type {
  Chat,
  ContextItem,
  Message,
  MessageAttachment,
  MessageReceipt,
  Project,
  WorkspaceSettings,
} from './types';
import { DEFAULT_GLOBAL_INSTRUCTIONS, isContextCacheStale } from './types';
import { attachmentSummary } from './attachments';
import {
  buildSessionBlock,
  type SessionBlockInput,
} from '@/lib/nostr/sessionBlock';
import { getPersona, type PersonaId } from './personas';

/** Soft budget for project context pack (chars). Pinned items win first. */
export const DEFAULT_CONTEXT_BUDGET = 12_000;

/** Grounded-only rule text (when enabled). */
export const GROUNDED_ONLY_BLOCK = [
  '# Grounded answers only',
  'Answer using only:',
  '1. Project instructions, memory, and context pack (if any)',
  '2. Thread instructions and conversation so far',
  '3. Attachments and Fetched Nostr / pack content in this request',
  '4. OpenDork session product rules',
  '',
  'If the answer is not supported by those sources, say you do not have enough project context and ask what to add — do not invent facts, APIs, or history outside them.',
  'When you use a source, mention it briefly (e.g. “From project memory…”, “From context: …”).',
].join('\n');

function formatContextItem(item: ContextItem): string {
  const title = item.title?.trim() || item.type;
  const body = item.content.trim();
  if (!body) return '';

  switch (item.type) {
    case 'url':
      return `### ${title}\nURL: ${body}`;
    case 'nostr': {
      const meta = item.meta;

      // Live network-search recipe — not a fetchable ref; expanded at send
      // time with keywords from the user's message (follows-scoped).
      if (meta?.pack === 'network-search') {
        const q = meta.snippet?.trim();
        return (
          `### ${title}\n` +
          `Live Nostr search across people the user follows` +
          (q ? ` — saved keywords: “${q}”` : ' — keywords come from each message') +
          `. Matching notes are injected as Attached/Fetched Nostr context at send time.`
        );
      }

      const stale = isContextCacheStale(meta);
      const lines = [`### ${title}`, `Nostr ref: ${body}`];
      if (meta?.pack) lines.push(`pack: ${meta.pack}`);
      if (meta?.displayName) lines.push(`name: ${meta.displayName}`);
      if (meta?.nip05) lines.push(`nip05: ${meta.nip05}`);
      if (meta?.about) lines.push(`about: ${meta.about}`);
      if (meta?.cachedText?.trim() && !stale) {
        lines.push('', meta.cachedText.trim());
      } else if (stale) {
        lines.push(
          '',
          '_(Snapshot is stale — this live pack is refreshed from relays at send time.)_',
        );
      } else if (meta?.snippet?.trim()) {
        lines.push('', meta.snippet.trim());
        lines.push(
          '',
          '_(Full content resolved from relays at send time when needed.)_',
        );
      } else {
        lines.push(
          '',
          '_(Ref will be resolved from relays at send time if not already in Fetched Nostr content.)_',
        );
      }
      return lines.join('\n');
    }
    default:
      return `### ${title}\n${body}`;
  }
}

/**
 * Resolve the global instructions layer.
 * Empty / missing → built-in baseline so the model always has a persona.
 */
export function resolveGlobalInstructions(
  settings?: Pick<WorkspaceSettings, 'globalInstructions'> | null,
): string {
  const custom = settings?.globalInstructions?.trim();
  return custom || DEFAULT_GLOBAL_INSTRUCTIONS;
}

/** Effective persona for a chat (chat override → workspace default → default). */
export function resolvePersonaId(
  chat?: Chat | null,
  settings?: Pick<WorkspaceSettings, 'defaultPersonaId'> | null,
): PersonaId | string {
  return chat?.personaId || settings?.defaultPersonaId || 'default';
}

/** Effective grounded-only flag (chat override → workspace). */
export function resolveGroundedOnly(
  chat?: Chat | null,
  settings?: Pick<WorkspaceSettings, 'groundedOnly'> | null,
): boolean {
  if (chat?.groundedOnly !== undefined) return !!chat.groundedOnly;
  return !!settings?.groundedOnly;
}

export interface PromptBuildOptions {
  contextBudget?: number;
  settings?: Pick<
    WorkspaceSettings,
    | 'globalInstructions'
    | 'includeNostrIdentity'
    | 'nostrIdentityDetail'
    | 'mentionAutoFetchNotes'
    | 'defaultPersonaId'
    | 'groundedOnly'
  > | null;
  /** @deprecated Use settings.globalInstructions */
  globalInstructions?: string;
  /** Extra system sections appended last (Nostr fetch, etc.) */
  extraBlocks?: string[];
  /** Session identity / product block input */
  session?: SessionBlockInput | null;
  /**
   * Force persona id for this build (otherwise from chat + settings).
   */
  personaId?: PersonaId | string | null;
  /**
   * Force grounded mode for this build.
   */
  groundedOnly?: boolean | null;
  /**
   * Layers / items to skip this turn.
   * Merged with chat.promptOmit. See Chat.promptOmit for key shapes.
   */
  omit?: string[] | null;
}

/** Merge chat + options omit lists (deduped). */
export function resolvePromptOmit(
  chat?: Chat | null,
  optionsOmit?: string[] | null,
): Set<string> {
  const set = new Set<string>();
  for (const k of chat?.promptOmit ?? []) {
    if (k) set.add(k);
  }
  for (const k of optionsOmit ?? []) {
    if (k) set.add(k);
  }
  return set;
}

export function isOmitted(omit: Set<string>, key: string): boolean {
  return omit.has(key);
}

/** Short label for a memory / context pack item. */
export function itemPreviewLabel(
  item: { title?: string; content?: string; type?: string },
  max = 48,
): string {
  const t = item.title?.trim();
  if (t) return t.length > max ? `${t.slice(0, max - 1)}…` : t;
  const body = (item.content ?? '').trim().replace(/\s+/g, ' ');
  if (body) return body.length > max ? `${body.slice(0, max - 1)}…` : body;
  return item.type || 'item';
}

export interface PromptBuildResult {
  content: string;
  /** Layers present in this system stack (for UI receipts / preview). */
  receipts: MessageReceipt[];
}

function pushReceipt(list: MessageReceipt[], r: MessageReceipt) {
  list.push(r);
}

/**
 * Build the system message stack (Claude-style) and collect receipts.
 *
 * 0. OpenDork session
 * 1. Global instructions
 * 2. Persona (optional)
 * 3. Grounded-only rule (optional)
 * 4. Project instructions / memory / context
 * 5. Thread override
 * 6. Extra blocks (Nostr packs, etc.)
 */
export function buildSystemContentDetailed(
  project: Project | null | undefined,
  chat?: Chat | null,
  options?: PromptBuildOptions,
): PromptBuildResult {
  const budget = options?.contextBudget ?? DEFAULT_CONTEXT_BUDGET;
  const parts: string[] = [];
  const receipts: MessageReceipt[] = [];
  const omit = resolvePromptOmit(chat, options?.omit);

  // 0. Session
  if (options?.session && !isOmitted(omit, 'session')) {
    const sessionText = buildSessionBlock({
      ...options.session,
      settings: options.settings ?? options.session.settings,
    });
    if (sessionText.trim()) {
      parts.push(sessionText);
      const identityOn =
        options.settings?.includeNostrIdentity !== false &&
        !!options.session.loggedIn;
      pushReceipt(receipts, {
        kind: 'session',
        label: 'Session',
        detail: identityOn
          ? 'App rules + your Nostr identity'
          : 'App rules',
      });
    }
  }

  if (!isOmitted(omit, 'global')) {
    const globalText =
      options?.globalInstructions !== undefined
        ? options.globalInstructions.trim() || DEFAULT_GLOBAL_INSTRUCTIONS
        : resolveGlobalInstructions(options?.settings);

    parts.push(`# Global instructions\n${globalText}`);
    pushReceipt(receipts, {
      kind: 'global',
      label: 'Instructions',
      detail: options?.settings?.globalInstructions?.trim()
        ? 'Your global instructions'
        : 'Built-in default',
    });
  }

  // Response style (persona overlay)
  if (!isOmitted(omit, 'persona')) {
    const personaId =
      options?.personaId !== undefined && options?.personaId !== null
        ? options.personaId
        : resolvePersonaId(chat, options?.settings);
    const persona = getPersona(personaId);
    if (persona.systemBlock.trim()) {
      parts.push(persona.systemBlock.trim());
      pushReceipt(receipts, {
        kind: 'persona',
        label: `Style · ${persona.label}`,
        detail: persona.description,
      });
    }
  }

  // Grounded mode
  if (!isOmitted(omit, 'grounded')) {
    const grounded =
      options?.groundedOnly !== undefined && options?.groundedOnly !== null
        ? !!options.groundedOnly
        : resolveGroundedOnly(chat, options?.settings);
    if (grounded) {
      parts.push(GROUNDED_ONLY_BLOCK);
      pushReceipt(receipts, {
        kind: 'grounded',
        label: 'Grounded',
        detail: 'Project + attachments only',
      });
    }
  }

  if (project) {
    const projectInstr = project.instructions?.trim();
    if (projectInstr && !isOmitted(omit, 'project_instr')) {
      parts.push(`# Project instructions\n${projectInstr}`);
      pushReceipt(receipts, {
        kind: 'project_instructions',
        label: 'Project instructions',
        detail: project.name,
      });
    }

    if (!isOmitted(omit, 'memory')) {
      const memoryItems = (project.memoryItems ?? []).filter(
        (m) => m.content.trim() && !isOmitted(omit, `memory:${m.id}`),
      );
      if (memoryItems.length > 0) {
        const bullets = memoryItems
          .map((m) => {
            const t = m.title?.trim();
            const body = m.content.trim();
            return t ? `- **${t}:** ${body}` : `- ${body}`;
          })
          .join('\n');
        parts.push(
          `# Project memory\nFacts to remember for this project (private to the user):\n${bullets}`,
        );
        pushReceipt(receipts, {
          kind: 'memory',
          label: 'Memory',
          count: memoryItems.length,
          detail: memoryItems
            .slice(0, 4)
            .map((m) => itemPreviewLabel(m, 36))
            .join(' · '),
        });
      }
    }

    if (!isOmitted(omit, 'context')) {
      const items = [...(project.contextItems ?? [])]
        .filter((c) => c.content.trim() && !isOmitted(omit, `context:${c.id}`))
        .sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;
          return a.createdAt - b.createdAt;
        });

      if (items.length > 0) {
        const blocks: string[] = [];
        const usedTitles: string[] = [];
        let used = 0;
        // Cap any single item so at least two can always ship (one huge
        // article must not evict every other pinned reference).
        const perItemCap = Math.floor(budget * 0.6);
        for (const item of items) {
          let block = formatContextItem(item);
          if (!block) continue;
          if (block.length > perItemCap) {
            block =
              block.slice(0, perItemCap) +
              '\n… [item truncated to fit the shared context budget]';
          }
          if (used + block.length > budget && blocks.length > 0) break;
          blocks.push(block);
          used += block.length + 2;
          usedTitles.push(itemPreviewLabel(item, 40));
        }
        if (blocks.length > 0) {
          parts.push(
            `# Project context\nThe following is reference material for this project:\n\n${blocks.join('\n\n')}`,
          );
          pushReceipt(receipts, {
            kind: 'context',
            label: 'Context pack',
            count: blocks.length,
            detail: usedTitles.slice(0, 4).join(' · '),
          });
        }
      }
    }
  }

  const override = chat?.instructionOverride?.trim();
  if (override && !isOmitted(omit, 'thread')) {
    parts.push(`# Thread instructions\n${override}`);
    pushReceipt(receipts, {
      kind: 'thread',
      label: 'Thread instructions',
    });
  }

  // Extra blocks (Nostr packs / mentions / auto-fetch). Skip when whole
  // ephemeral layers are omitted — caller may also pass empty extraBlocks.
  const skipExtras =
    isOmitted(omit, 'nostr_chips') &&
    isOmitted(omit, 'mentions') &&
    isOmitted(omit, 'attachments');
  if (!skipExtras) {
    for (const block of options?.extraBlocks ?? []) {
      const t = block?.trim();
      if (!t) continue;
      const head = t.slice(0, 80).toLowerCase();
      if (
        (head.includes('mention') || head.includes('@')) &&
        isOmitted(omit, 'mentions')
      ) {
        continue;
      }
      if (
        (head.includes('pack') ||
          head.includes('nostr context') ||
          head.includes('fetched nostr') ||
          head.includes('# fetched')) &&
        isOmitted(omit, 'nostr_chips')
      ) {
        continue;
      }
      parts.push(t);
      if (head.includes('fetched nostr') || head.includes('# fetched')) {
        pushReceipt(receipts, {
          kind: 'nostr_auto',
          label: 'Fetched Nostr',
        });
      } else if (head.includes('mention') || head.includes('@')) {
        pushReceipt(receipts, {
          kind: 'nostr_mention',
          label: 'Mentions',
        });
      } else if (head.includes('pack') || head.includes('nostr context')) {
        pushReceipt(receipts, {
          kind: 'nostr_pack',
          label: 'Nostr packs',
        });
      } else {
        pushReceipt(receipts, {
          kind: 'nostr_pack',
          label: 'Extra context',
        });
      }
    }
  }

  return {
    content: parts.join('\n\n'),
    receipts,
  };
}

/**
 * Build the system message stack (Claude-style).
 * Prefer buildSystemContentDetailed when you need receipts.
 */
export function buildSystemContent(
  project: Project | null | undefined,
  chat?: Chat | null,
  options?: PromptBuildOptions,
): string {
  return buildSystemContentDetailed(project, chat, options).content;
}

/** Nested item under a preview layer (memory row, context pack entry, …). */
export interface ContextPreviewItem {
  id: string;
  /** Omit key, e.g. memory:abc or context:xyz */
  key: string;
  label: string;
  detail?: string;
  /** false when toggled off for this chat/turn */
  enabled: boolean;
}

/** One layer shown in the composer “what will be sent” preview. */
export interface ContextPreviewLayer {
  id: string;
  label: string;
  detail?: string;
  count?: number;
  /**
   * Whether this layer will be injected.
   * false = user toggled it off (still listed so it can be re-enabled).
   */
  enabled: boolean;
  /** @deprecated use enabled */
  active: boolean;
  /** Can the user toggle this layer off? */
  toggleable?: boolean;
  /** Nested items (context pack entries, memories) */
  items?: ContextPreviewItem[];
}

/**
 * Snapshot of what the next send will inject — for the composer preview strip.
 * Does not include live Nostr fetch bodies (unknown until send).
 * Layers stay listed when omitted so the UI can re-enable them.
 */
export function buildContextPreview(opts: {
  project?: Project | null;
  chat?: Chat | null;
  settings?: WorkspaceSettings | null;
  sessionLoggedIn?: boolean;
  chipCount?: number;
  mentionCount?: number;
  attachmentCount?: number;
  /** Optional titles for ephemeral chips / attachments */
  chipLabels?: string[];
  mentionLabels?: string[];
  attachmentLabels?: string[];
  /** Extra omit keys (merged with chat.promptOmit) */
  omit?: string[] | null;
}): ContextPreviewLayer[] {
  const layers: ContextPreviewLayer[] = [];
  const settings = opts.settings;
  const chat = opts.chat;
  const project = opts.project;
  const omit = resolvePromptOmit(chat, opts.omit);

  const pushLayer = (
    layer: Omit<ContextPreviewLayer, 'active' | 'enabled'> & {
      enabled?: boolean;
      toggleable?: boolean;
    },
  ) => {
    const enabled =
      layer.enabled !== undefined
        ? layer.enabled
        : !isOmitted(omit, layer.id);
    layers.push({
      ...layer,
      enabled,
      active: enabled,
      toggleable: layer.toggleable !== false,
    });
  };

  const identityOn =
    settings?.includeNostrIdentity !== false && !!opts.sessionLoggedIn;
  pushLayer({
    id: 'session',
    label: 'Session',
    detail: identityOn
      ? 'App rules + your Nostr identity'
      : 'App rules',
  });

  pushLayer({
    id: 'global',
    label: 'Instructions',
    detail: settings?.globalInstructions?.trim()
      ? 'Your global instructions'
      : 'Built-in default',
  });

  if (project) {
    if (project.instructions?.trim()) {
      pushLayer({
        id: 'project_instr',
        label: 'Project instructions',
        detail: project.name,
      });
    }
    const mem = (project.memoryItems ?? []).filter((m) => m.content.trim());
    if (mem.length) {
      const items: ContextPreviewItem[] = mem.map((m) => {
        const key = `memory:${m.id}`;
        return {
          id: m.id,
          key,
          label: itemPreviewLabel(m),
          enabled: !isOmitted(omit, 'memory') && !isOmitted(omit, key),
        };
      });
      const enabledCount = items.filter((i) => i.enabled).length;
      pushLayer({
        id: 'memory',
        label: 'Memory',
        count: mem.length,
        detail:
          enabledCount === 0
            ? 'Off for this chat'
            : items
                .filter((i) => i.enabled)
                .slice(0, 3)
                .map((i) => i.label)
                .join(' · '),
        items,
        enabled: !isOmitted(omit, 'memory') && enabledCount > 0,
      });
    }
    const ctx = [...(project.contextItems ?? [])]
      .filter((c) => c.content.trim())
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return a.createdAt - b.createdAt;
      });
    if (ctx.length) {
      const items: ContextPreviewItem[] = ctx.map((c) => {
        const key = `context:${c.id}`;
        const typeHint =
          c.type === 'url' ? 'URL' : c.type === 'nostr' ? 'Nostr' : 'Note';
        return {
          id: c.id,
          key,
          label: itemPreviewLabel(c),
          detail: typeHint,
          enabled: !isOmitted(omit, 'context') && !isOmitted(omit, key),
        };
      });
      const enabledCount = items.filter((i) => i.enabled).length;
      pushLayer({
        id: 'context',
        label: 'Context pack',
        count: ctx.length,
        detail:
          enabledCount === 0
            ? 'Off for this chat'
            : items
                .filter((i) => i.enabled)
                .slice(0, 3)
                .map((i) => i.label)
                .join(' · '),
        items,
        enabled: !isOmitted(omit, 'context') && enabledCount > 0,
      });
    }
  }

  if (chat?.instructionOverride?.trim()) {
    pushLayer({
      id: 'thread',
      label: 'Thread instructions',
    });
  }

  const chips = opts.chipCount ?? 0;
  const ments = opts.mentionCount ?? 0;
  if (chips > 0) {
    const labels = opts.chipLabels ?? [];
    pushLayer({
      id: 'nostr_chips',
      label: 'Nostr packs',
      count: chips,
      detail: labels.slice(0, 3).join(' · ') || undefined,
      items: labels.map((label, i) => ({
        id: `chip-${i}`,
        key: `nostr_chips:${i}`,
        label,
        enabled: !isOmitted(omit, 'nostr_chips'),
      })),
    });
  }
  if (ments > 0) {
    const labels = opts.mentionLabels ?? [];
    pushLayer({
      id: 'mentions',
      label: '@mentions',
      count: ments,
      detail: labels.slice(0, 3).join(' · ') || undefined,
      items: labels.map((label, i) => ({
        id: `mention-${i}`,
        key: `mentions:${i}`,
        label,
        enabled: !isOmitted(omit, 'mentions'),
      })),
    });
  }
  const atts = opts.attachmentCount ?? 0;
  if (atts > 0) {
    const labels = opts.attachmentLabels ?? [];
    pushLayer({
      id: 'attachments',
      label: 'Attachments',
      count: atts,
      detail: labels.slice(0, 3).join(' · ') || undefined,
      items: labels.map((label, i) => ({
        id: `att-${i}`,
        key: `attachments:${i}`,
        label,
        enabled: !isOmitted(omit, 'attachments'),
      })),
    });
  }

  return layers;
}

/** Estimate chars of context that will be injected (for UI). */
export function estimateContextChars(
  project: Project | null | undefined,
  settings?: Pick<WorkspaceSettings, 'globalInstructions'> | null,
): {
  items: number;
  /** Chars actually injected (clamped to the budget) */
  chars: number;
  /** Unclamped total — compare against DEFAULT_CONTEXT_BUDGET for meters */
  rawChars: number;
  instructionsChars: number;
  globalChars: number;
  memoryChars: number;
} {
  const globalText = resolveGlobalInstructions(settings);
  const instructions = project?.instructions?.trim() || '';
  const items = project?.contextItems ?? [];
  const memories = (project?.memoryItems ?? [])
    .map((m) => m.content.trim())
    .filter(Boolean);
  let chars = 0;
  for (const item of items) {
    chars += formatContextItem(item).length;
  }
  const memoryChars = memories.reduce((n, m) => n + m.length + 2, 0);
  return {
    items: items.filter((i) => i.content.trim()).length,
    chars: Math.min(chars, DEFAULT_CONTEXT_BUDGET),
    rawChars: chars,
    instructionsChars: instructions.length,
    globalChars: globalText.length,
    memoryChars,
  };
}

/** Build text parts for file attachments (images stay multimodal). */
function textFromAttachments(attachments: MessageAttachment[] | undefined): string {
  if (!attachments?.length) return '';
  const blocks: string[] = [];
  for (const a of attachments) {
    if (a.kind === 'image') continue;
    const name = a.name?.trim() || 'file';
    if (a.kind === 'text' && a.data) {
      blocks.push(`### Attached file: ${name}\n\`\`\`\n${a.data}\n\`\`\``);
    } else {
      blocks.push(`### Attached file: ${name}\n(binary or unsupported — not inlined)`);
    }
  }
  return blocks.join('\n\n');
}

/**
 * Convert a stored message into an OpenAI-compatible chat message.
 * Images become multimodal content parts when present.
 */
export function messageToApiContent(msg: Message): ChatMessage['content'] {
  const attachments = msg.attachments ?? [];
  const images = attachments.filter((a) => a.kind === 'image' && a.data);
  const fileText = textFromAttachments(attachments);
  const body = [msg.content?.trim() || '', fileText].filter(Boolean).join('\n\n');

  if (msg.role !== 'user' || images.length === 0) {
    return body || msg.content || '';
  }

  const parts: Array<{
    type: 'text' | 'image_url';
    text?: string;
    image_url?: { url: string };
  }> = [];

  if (body) {
    parts.push({ type: 'text', text: body });
  } else {
    parts.push({ type: 'text', text: '(see attached image)' });
  }
  for (const img of images) {
    parts.push({
      type: 'image_url',
      image_url: { url: img.data },
    });
  }
  return parts;
}

/**
 * Assemble API messages for a completion request.
 * Returns messages + receipts for the system stack (caller may merge attachment receipts).
 */
export function buildApiMessagesDetailed(
  project: Project | null | undefined,
  chat: Chat | null | undefined,
  messages: Message[],
  options?: PromptBuildOptions,
): { messages: ChatMessage[]; receipts: MessageReceipt[] } {
  const { content, receipts } = buildSystemContentDetailed(project, chat, options);
  const omit = resolvePromptOmit(chat, options?.omit);
  const stripAttachments = isOmitted(omit, 'attachments');
  const api: ChatMessage[] = [
    {
      role: 'system',
      content,
    },
  ];

  let attachmentCount = 0;
  for (const msg of messages) {
    if (msg.role === 'system') continue;
    const msgForApi =
      stripAttachments && msg.attachments?.length
        ? { ...msg, attachments: undefined }
        : msg;
    const contentPart = messageToApiContent(msgForApi);
    const empty =
      (typeof contentPart === 'string' && !contentPart.trim()) ||
      (Array.isArray(contentPart) && contentPart.length === 0);
    if (empty && msg.role === 'assistant') continue;
    if (
      !stripAttachments &&
      msg.role === 'user' &&
      msg.attachments?.length
    ) {
      attachmentCount += msg.attachments.length;
    }
    api.push({
      role: msg.role === 'assistant' ? 'assistant' : 'user',
      content: empty ? '' : contentPart,
    });
  }

  const finalReceipts = [...receipts];
  if (attachmentCount > 0) {
    finalReceipts.push({
      kind: 'attachment',
      label: 'Attachments',
      count: attachmentCount,
    });
  }

  return { messages: api, receipts: finalReceipts };
}

/**
 * Assemble API messages for a completion request.
 */
export function buildApiMessages(
  project: Project | null | undefined,
  chat: Chat | null | undefined,
  messages: Message[],
  options?: PromptBuildOptions,
): ChatMessage[] {
  return buildApiMessagesDetailed(project, chat, messages, options).messages;
}

/** Auto-title from first user message. */
export function titleFromMessage(
  content: string,
  maxLen = 48,
  attachments?: MessageAttachment[],
): string {
  const cleaned = content.replace(/\s+/g, ' ').trim();
  if (cleaned) {
    if (cleaned.length <= maxLen) return cleaned;
    return cleaned.slice(0, maxLen - 1).trimEnd() + '…';
  }
  const summary = attachmentSummary(attachments);
  if (summary) {
    const t = `Attachment: ${summary}`;
    return t.length <= maxLen ? t : t.slice(0, maxLen - 1) + '…';
  }
  return 'New chat';
}

/** Clip text for memory / context pin (keep useful length). */
export function clipForPin(text: string, maxLen = 1200): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen - 1).trimEnd() + '…';
}
